import { useState } from 'react'
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  IconButton, 
  Menu, 
  MenuItem, 
  Avatar, 
  Divider, 
  Container,
  Button,
  useTheme,
  Badge,
  alpha,
  Tooltip,
  useMediaQuery
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon,
  CheckroomOutlined as FashionIcon,
  NotificationsOutlined as NotificationsIcon,
  CreditCardOutlined as CreditIcon,
  DarkModeOutlined as DarkModeIcon,
  PersonOutlined as PersonIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  HelpOutlineOutlined as HelpIcon,
  Language as LanguageIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../../contexts/LanguageContext'
import { useAuth } from '../../contexts/AuthContext'
import CreditsDisplay from '../credits/CreditsDisplay'

interface AppHeaderProps {
  title: string
  username?: string | null
  showAdminButton?: boolean
  showBackButton?: boolean
  onBackClick?: () => void
  onLogout: () => void
}

const AppHeader = ({
  title,
  username,
  showAdminButton = false,
  showBackButton = false,
  onBackClick,
  onLogout
}: AppHeaderProps) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { lang, setLang, t } = useLanguage()
  const { hasModuleAccess, refreshPermissions } = useAuth()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null)
  const [creditsAnchorEl, setCreditsAnchorEl] = useState<null | HTMLElement>(null)
  const [creditsRefresh, setCreditsRefresh] = useState(0)
  
  const profileOpen = Boolean(anchorEl)
  const notificationsOpen = Boolean(notificationsAnchorEl)
  const creditsOpen = Boolean(creditsAnchorEl)
  
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleNotificationsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget)
  }

  const handleCreditsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setCreditsAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  
  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null)
  }
  
  const handleCreditsClose = () => {
    setCreditsAnchorEl(null)
  }
  
  const handleAdminPanel = () => {
    handleClose()
    navigate('/admin')
  }
  
  const handleLogout = () => {
    handleClose()
    onLogout()
  }
  
  const handleBack = () => {
    if (onBackClick) {
      onBackClick()
    } else {
      navigate(-1)
    }
  }
  
  const handleHomeClick = () => {
    navigate('/')
  }
  
  const handleBuyCredits = () => {
    handleCreditsClose()
    // Navigate to credits purchase page
    console.log('Navigate to credits purchase page')
  }

  const toggleLanguage = () => {
    const langCycle: Record<'pl' | 'en' | 'de', 'pl' | 'en' | 'de'> = {
      pl: 'en',
      en: 'de',
      de: 'pl'
    }
    setLang(langCycle[lang])
  }

  return (
    <AppBar 
      position="sticky" 
      elevation={0}
      sx={{
        backgroundColor: 'white',
        color: 'text.primary',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ height: 70 }}>
          {/* Logo and Title Section */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mr: { xs: 1, md: 3 }
            }}
          >
            {showBackButton ? (
              <IconButton
                color="primary"
                aria-label="back"
                onClick={handleBack}
                sx={{ 
                  mr: { xs: 1, md: 2 },
                  color: theme.palette.primary.main
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            ) : (
              <FashionIcon 
                sx={{ 
                  fontSize: { xs: 28, md: 32 }, 
                  mr: { xs: 1, md: 2 },
                  color: theme.palette.primary.main
                }} 
                onClick={handleHomeClick}
              />
            )}
            
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              component="div" 
              onClick={handleHomeClick}
              sx={{ 
                fontWeight: 600,
                color: theme.palette.primary.main,
                letterSpacing: '-0.02em',
                cursor: 'pointer',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              YourModel.eu
            </Typography>
          </Box>
          
          {/* Center Section - Title/Navigation */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            {/* On mobile, show page title */}
            {isMobile ? (
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {title}
              </Typography>
            ) : (
              /* On desktop, show page title with better styling */
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 500, 
                  color: 'text.primary',
                  ml: 2,
                  borderLeft: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                  pl: 2
                }}
              >
                {title}
              </Typography>
            )}
          </Box>
          
          {/* Right Section - User Tools */}
          {username && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Credits Balance */}
              {hasModuleAccess('credits') && !isMobile && (
                <Tooltip title={t('header.credits')}>
                  <Box
                    onClick={handleCreditsMenu}
                    sx={{
                      mr: 1.5,
                      cursor: 'pointer',
                      '& .MuiChip-root': {
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }
                    }}
                  >
                    <CreditsDisplay variant="compact" refreshTrigger={creditsRefresh} />
                  </Box>
                </Tooltip>
              )}

              {hasModuleAccess('credits') && (
              <Menu
                anchorEl={creditsAnchorEl}
                open={creditsOpen}
                onClose={handleCreditsClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    mt: 1.5,
                    borderRadius: 2,
                    minWidth: 250,
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CreditsDisplay variant="full" refreshTrigger={creditsRefresh} />
                <Box sx={{ px: 2, pb: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="medium"
                    onClick={handleBuyCredits}
                    startIcon={<WalletIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    Buy More Credits
                  </Button>
                </Box>
                <Divider />
                <MenuItem onClick={handleCreditsClose} dense>
                  <Typography variant="body2">View Transaction History</Typography>
                </MenuItem>
                <MenuItem onClick={handleCreditsClose} dense>
                  <Typography variant="body2">Redeem Promo Code</Typography>
                </MenuItem>
              </Menu>
              )}

              {/* Language Switcher */}
              <Tooltip title={t('header.language')}>
                <IconButton
                  onClick={toggleLanguage}
                  color="default"
                  size="medium"
                  sx={{ mr: { xs: 1, md: 1.5 } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LanguageIcon sx={{ fontSize: 20 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      {lang.toUpperCase()}
                    </Typography>
                  </Box>
                </IconButton>
              </Tooltip>

              {/* Notifications */}
              <Tooltip title="Notifications">
                <IconButton
                  onClick={handleNotificationsMenu}
                  color="default"
                  size="medium"
                  sx={{ mr: { xs: 1, md: 1.5 } }}
                >
                  <Badge badgeContent={3} color="error">
                    <NotificationsIcon sx={{ fontSize: 22 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
              
              <Menu
                anchorEl={notificationsAnchorEl}
                open={notificationsOpen}
                onClose={handleNotificationsClose}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    mt: 1.5,
                    borderRadius: 2,
                    minWidth: 280,
                    maxWidth: 320,
                    maxHeight: 360,
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                    overflow: 'auto'
                  }
                }}
              >
                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Notifications
                  </Typography>
                </Box>
                <MenuItem sx={{ py: 1.5 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Your image generation is complete
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      2 minutes ago
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem sx={{ py: 1.5 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      New credits added to your account
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      1 hour ago
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem sx={{ py: 1.5 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Welcome to Fashion Babe!
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      2 days ago
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem sx={{ justifyContent: 'center' }}>
                  <Typography variant="body2" color="primary">
                    View all notifications
                  </Typography>
                </MenuItem>
              </Menu>
              
              {/* User Profile */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {!isMobile && (
                  <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary', fontWeight: 500 }}>
                    {username}
                  </Typography>
                )}
                
                <IconButton
                  onClick={handleMenu}
                  color="default"
                  size="medium"
                  edge="end"
                  sx={{ 
                    ml: 0.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }
                  }}
                >
                  <Avatar 
                    sx={{ 
                      width: 30, 
                      height: 30, 
                      bgcolor: theme.palette.primary.main,
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }}
                  >
                    {username.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
                
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  open={profileOpen}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      mt: 1.5,
                      borderRadius: 2,
                      minWidth: 200,
                      boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
                    <Avatar 
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        bgcolor: theme.palette.primary.main,
                        mx: 'auto',
                        mb: 1
                      }}
                    >
                      {username.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {username}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fashion Designer
                    </Typography>
                  </Box>
                  
                  <Divider />
                  
                  <MenuItem onClick={handleClose} sx={{ py: 1.25 }}>
                    <PersonIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                    <Typography variant="body2">My Profile</Typography>
                  </MenuItem>

                  {hasModuleAccess('credits') && isMobile && (
                    <MenuItem onClick={handleCreditsMenu} sx={{ py: 1.25 }}>
                      <CreditIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                      <Typography variant="body2">{t('header.credits')}</Typography>
                    </MenuItem>
                  )}
                  
                  <MenuItem onClick={handleClose} sx={{ py: 1.25 }}>
                    <HelpIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                    <Typography variant="body2">Help & Support</Typography>
                  </MenuItem>
                  
                  <MenuItem onClick={handleClose} sx={{ py: 1.25 }}>
                    <DarkModeIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                    <Typography variant="body2">Toggle Theme</Typography>
                  </MenuItem>
                  
                  {showAdminButton && (
                    <MenuItem onClick={handleAdminPanel} sx={{ py: 1.25 }}>
                      <AdminIcon fontSize="small" sx={{ mr: 1.5, color: theme.palette.warning.main }} />
                      <Typography variant="body2" fontWeight={500} color="warning.main">
                        Admin Panel
                      </Typography>
                    </MenuItem>
                  )}

                  {showAdminButton && (
                    <MenuItem onClick={() => { handleClose(); refreshPermissions(); }} sx={{ py: 1.25 }}>
                      <AdminIcon fontSize="small" sx={{ mr: 1.5, color: theme.palette.info.main }} />
                      <Typography variant="body2" fontWeight={500} color="info.main">
                        Refresh Permissions
                      </Typography>
                    </MenuItem>
                  )}
                  
                  <Divider />
                  
                  <MenuItem onClick={handleLogout} sx={{ py: 1.25 }}>
                    <LogoutIcon fontSize="small" sx={{ mr: 1.5, color: theme.palette.error.main }} />
                    <Typography variant="body2" color="error">
                      {t('header.logout')}
                    </Typography>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  )
}

export default AppHeader
